import { App as BaseApp, LanguageSwitcher, type NavItem, navigate, useRouter } from '@base-app/web2'
import { lazy, memo, Suspense, useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PageSkeleton } from './components/molecules'
import { Sidebar } from './components/organisms'
import { AdminLayout } from './components/templates'
import { useAuth } from './hooks/useAuth'
import { SelectedAppProvider, useSelectedApp } from './hooks/useSelectedApp'
import { ToastProvider } from './hooks/useToast'
import { useViewerApp } from './hooks/useViewerApp'
import { refetchQuietly } from './lib/apolloRefetch'
import { viewerOpensSection } from './lib/viewerPermissions'

const ApplicationsPage = lazy(() =>
  import('./pages/Applications').then(m => ({ default: m.ApplicationsPage })),
)
const ApplicationPage = lazy(() =>
  import('./pages/ApplicationPage').then(m => ({ default: m.ApplicationPage })),
)
const AppsPage = lazy(() => import('./pages/Apps').then(m => ({ default: m.AppsPage })))
const ClientsPage = lazy(() => import('./pages/Clients').then(m => ({ default: m.ClientsPage })))
const CollaboratorFunctionsPage = lazy(() =>
  import('./pages/CollaboratorFunctions').then(m => ({ default: m.CollaboratorFunctionsPage })),
)
const CollaboratorsPage = lazy(() =>
  import('./pages/Collaborators').then(m => ({ default: m.CollaboratorsPage })),
)
const DepartmentsPage = lazy(() =>
  import('./pages/Departments').then(m => ({ default: m.DepartmentsPage })),
)
const LocationsPage = lazy(() =>
  import('./pages/Locations').then(m => ({ default: m.LocationsPage })),
)
const BannersPage = lazy(() => import('./pages/Banners').then(m => ({ default: m.BannersPage })))
const EventsPage = lazy(() => import('./pages/Events').then(m => ({ default: m.EventsPage })))
const OfferingsPage = lazy(() =>
  import('./pages/Offerings').then(m => ({ default: m.OfferingsPage })),
)
const FeedPage = lazy(() => import('./pages/Feed').then(m => ({ default: m.FeedPage })))
const RadiosPage = lazy(() => import('./pages/Radios').then(m => ({ default: m.RadiosPage })))
const DashboardPage = lazy(() =>
  import('./pages/Dashboard').then(m => ({ default: m.DashboardPage })),
)
const LoginPage = lazy(() => import('./pages/Login').then(m => ({ default: m.LoginPage })))
const PodcastsPage = lazy(() => import('./pages/Podcasts').then(m => ({ default: m.PodcastsPage })))
// TODO(v2): Re-enable public registration when self-sign-up is supported.
// const RegisterPage = lazy(() => import('./pages/Register').then(m => ({ default: m.RegisterPage })))
const VideoPlaylistsPage = lazy(() =>
  import('./pages/VideoPlaylists').then(m => ({ default: m.VideoPlaylistsPage })),
)
const VideosPage = lazy(() => import('./pages/Videos').then(m => ({ default: m.VideosPage })))
const AdministrationUsersPage = lazy(() =>
  import('./pages/AdministrationUsers').then(m => ({ default: m.AdministrationUsersPage })),
)
const AppUsersPage = lazy(() => import('./pages/AppUsers').then(m => ({ default: m.AppUsersPage })))
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })),
)
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })),
)
const SettingsMePage = lazy(() =>
  import('./pages/SettingsMe').then(m => ({ default: m.SettingsMePage })),
)

export function parseClientApplicationsPath(path: string): string | null {
  const match = path.match(/^\/clients\/([^/]+)\/applications$/)
  return match ? match[1] : null
}

// /clients/<clientId>/applications/new or /clients/<clientId>/applications/<appId>: the page
// that creates or edits one application (appId is null when creating)
export function parseClientApplicationPagePath(
  path: string,
): { clientId: string; appId: string | null } | null {
  const match = path.match(/^\/clients\/([^/]+)\/applications\/([^/]+)$/)
  if (!match) {
    return null
  }
  return { clientId: match[1], appId: match[2] === 'new' ? null : match[2] }
}

export function parseClientAppResourcePath(path: string): {
  clientId: string
  appId: string
  resource:
    | 'videos'
    | 'podcasts'
    | 'playlists'
    | 'collaborators'
    | 'collaborator-functions'
    | 'departments'
    | 'locations'
    | 'radios'
    | 'users'
    | 'banners'
    | 'events'
    | 'offerings'
    | 'feed'
} | null {
  const match = path.match(
    /^\/clients\/([^/]+)\/apps\/([^/]+)\/(videos|podcasts|playlists|collaborators|collaborator-functions|departments|locations|radios|users|banners|events|offerings|feed)$/,
  )
  if (!match) {
    return null
  }
  return {
    clientId: match[1],
    appId: match[2],
    resource: match[3] as
      | 'videos'
      | 'podcasts'
      | 'playlists'
      | 'collaborators'
      | 'collaborator-functions'
      | 'departments'
      | 'locations'
      | 'radios'
      | 'users'
      | 'banners'
      | 'events'
      | 'offerings'
      | 'feed',
  }
}

export const AppRoutes = memo(
  ({
    isAuthenticated,
    authChecked,
    userName,
    isAdminUser,
  }: {
    isAuthenticated: boolean
    authChecked: boolean
    userName: string
    // Read from AppContent's auth query, which has settled before routes render; a second
    // useAuth() here would start empty and bounce /administration/users to the dashboard
    isAdminUser: boolean
  }) => {
    const { currentPath } = useRouter()
    // The application of a scoped section, with what the API says this viewer may open in it
    const scoped = parseClientAppResourcePath(currentPath)
    const {
      app: viewerApp,
      loading: viewerLoading,
      refetch: refetchViewerApp,
    } = useViewerApp(authChecked && isAuthenticated ? scoped?.appId : undefined)
    // The cached answer is only a head start: permissions and accesses can change outside this
    // tab, so every scoped navigation asks the API again (one small Application request). The
    // sidebar and the page share the normalized entry, so they follow whatever the answer says.
    // A first load without a cache entry is already being fetched, so it isn't asked twice.
    // biome-ignore lint/correctness/useExhaustiveDependencies: runs on navigation only, not on every answer
    useEffect(() => {
      if (scoped && viewerApp) {
        // Fire and forget: a failed refresh keeps the last answer (useViewerApp), so the
        // rejection is only swallowed here
        refetchQuietly(refetchViewerApp())
      }
    }, [currentPath])

    const redirectTo = (() => {
      if (
        authChecked &&
        !isAuthenticated &&
        currentPath !== '/login' &&
        currentPath !== '/forgot-password' &&
        !currentPath.startsWith('/reset-password/')
      ) {
        return '/login'
      }
      if (
        authChecked &&
        isAuthenticated &&
        currentPath === '/login'
        // || currentPath === '/register' // TODO(v2): uncomment when self-sign-up is re-enabled
      ) {
        return '/'
      }
      // Clients, Apps and admin users are global-admin pages; the API refuses them to others
      const isAdminOnlyPath =
        currentPath === '/administration/users' ||
        currentPath === '/clients' ||
        currentPath === '/apps' ||
        parseClientApplicationsPath(currentPath) !== null ||
        parseClientApplicationPagePath(currentPath) !== null
      if (authChecked && isAuthenticated && isAdminOnlyPath && !isAdminUser) {
        return '/'
      }
      // A scoped section opens only when the API's viewerPermissions (viewerCanManageUsers for
      // Users) include it; wait for the application before deciding
      if (
        authChecked &&
        isAuthenticated &&
        scoped &&
        !viewerLoading &&
        !viewerOpensSection(viewerApp, scoped.resource)
      ) {
        return '/'
      }
      return null
    })()

    useLayoutEffect(() => {
      if (redirectTo) {
        navigate(redirectTo)
      }
    }, [redirectTo])

    if (redirectTo || (scoped && viewerLoading)) {
      return null
    }

    if (currentPath === '/login') {
      return <LoginPage />
    }
    // TODO(v2): Re-enable when self-sign-up is supported.
    // if (currentPath === '/register') {
    //   return <RegisterPage />
    // }
    if (currentPath === '/forgot-password') {
      return <ForgotPasswordPage />
    }
    if (currentPath.startsWith('/reset-password/')) {
      return <ResetPasswordPage />
    }

    if (currentPath === '/administration/users') {
      return <AdministrationUsersPage />
    }

    if (currentPath === '/settings/me') {
      return <SettingsMePage />
    }

    const resourceMatch = parseClientAppResourcePath(currentPath)
    if (resourceMatch) {
      const { clientId: rClientId, appId, resource } = resourceMatch
      // A new client or app remounts the page, so its search and page start over
      const listKey = `${rClientId}:${appId}`
      if (resource === 'videos') {
        return <VideosPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'podcasts') {
        return <PodcastsPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'playlists') {
        return <VideoPlaylistsPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'collaborators') {
        return <CollaboratorsPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'collaborator-functions') {
        return <CollaboratorFunctionsPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'departments') {
        return <DepartmentsPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'locations') {
        return <LocationsPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'radios') {
        return <RadiosPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'users') {
        return <AppUsersPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'banners') {
        return <BannersPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'events') {
        return <EventsPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'offerings') {
        return <OfferingsPage key={listKey} clientId={rClientId} appId={appId} />
      }
      if (resource === 'feed') {
        return <FeedPage key={listKey} clientId={rClientId} appId={appId} />
      }
    }

    const applicationPage = parseClientApplicationPagePath(currentPath)
    if (applicationPage) {
      return (
        <ApplicationPage
          key={`${applicationPage.clientId}:${applicationPage.appId ?? 'new'}`}
          clientId={applicationPage.clientId}
          appId={applicationPage.appId ?? undefined}
        />
      )
    }

    const clientId = parseClientApplicationsPath(currentPath)
    if (clientId) {
      return <ApplicationsPage key={clientId} clientId={clientId} />
    }
    if (currentPath === '/clients') {
      return <ClientsPage />
    }
    if (currentPath === '/apps') {
      return <AppsPage />
    }

    return <DashboardPage userName={userName} />
  },
)
AppRoutes.displayName = 'AppRoutes'

const AppContent = () => {
  const { isAuthenticated, authChecked, user, logout } = useAuth()
  const { clearSelectedApp } = useSelectedApp()
  const { t } = useTranslation('admin-mobifull')

  // The next person to sign in on this browser must not start on this user's app
  const handleLogout = useCallback(async () => {
    clearSelectedApp()
    await logout()
  }, [clearSelectedApp, logout])
  const isAdminUser = !!(
    user?.features?.includes('superadmin') || user?.features?.includes('admin')
  )

  const navItems: NavItem[] = useMemo(
    () => [
      { to: '/', label: t('nav.dashboard'), isPrivate: true, hideIfAuth: true },
      { to: '/clients', label: t('nav.clients'), isPrivate: true, hideIfAuth: true },
      { to: '/apps', label: t('nav.apps'), isPrivate: true, hideIfAuth: true },
      { to: '/login', label: t('nav.login'), hideIfAuth: true },
    ],
    [t],
  )

  const routesEl = authChecked ? (
    <Suspense fallback={<PageSkeleton />}>
      <AppRoutes
        isAuthenticated={isAuthenticated}
        authChecked={authChecked}
        userName={user?.username ?? ''}
        isAdminUser={isAdminUser}
      />
    </Suspense>
  ) : null

  return (
    <BaseApp
      navItems={navItems}
      isAuthenticated={isAuthenticated}
      onLogout={handleLogout}
      endSlot={<LanguageSwitcher />}
      brand={t('footer.brand')}>
      {isAuthenticated && authChecked ? (
        <AdminLayout sidebar={<Sidebar userName={user?.username ?? ''} />}>{routesEl}</AdminLayout>
      ) : (
        routesEl
      )}
    </BaseApp>
  )
}

export const App = () => (
  <ToastProvider>
    <SelectedAppProvider>
      <AppContent />
    </SelectedAppProvider>
  </ToastProvider>
)
